import React, { useMemo } from 'react';
import {
  FormDialog,
  IMAGE_FILE_ACCEPT,
  RhfFileInput,
  RhfTextField,
  toast,
} from '@core/components';
import type { Collar } from '../../api/collar';
import { createCollar, updateCollar } from '../../api/collar';
import type { Vestment } from '../../api/vestment';
import { isRequiredCollar } from '../utils/collar';
import { getImageThumbnailUrl } from '../utils/image';

interface CollarDialogProps {
  open: boolean;
  onClose: () => void;
  vestment: Vestment;
  collar?: Collar | null;
  onSuccess?: () => void;
}

type CollarFormValues = {
  name: string;
  image: File | null;
};

const CollarDialog: React.FC<CollarDialogProps> = ({
  open,
  onClose,
  vestment,
  collar,
  onSuccess,
}) => {
  const defaultValues = useMemo<CollarFormValues>(
    () => ({
      name: collar?.name ?? '',
      image: null,
    }),
    [collar]
  );
  const lockedName = collar ? isRequiredCollar(collar, vestment.type) : false;

  const onSubmit = async (data: CollarFormValues) => {
    const name = data.name.trim();
    try {
      if (collar) {
        await updateCollar(collar.id, {
          name,
          image: data.image ?? undefined,
        });
        toast.success('Collar updated');
      } else {
        await createCollar({
          vestment: vestment.id,
          name,
          image: data.image ?? undefined,
        });
        toast.success(`"${name}" added`);
      }
      onSuccess?.();
    } catch (error) {
      toast.apiError('Failed to save collar.', error);
      throw error;
    }
  };

  return (
    <FormDialog<CollarFormValues>
      open={open}
      onClose={onClose}
      title={
        collar ? `Edit ${vestment.name} Collar` : `Add ${vestment.name} Collar`
      }
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={collar ? 'Update' : 'Add'}
      maxWidth="xs"
      addAnother={!collar}
    >
      <RhfTextField<CollarFormValues>
        name="name"
        label="Name"
        required
        disabled={lockedName}
        textFieldProps={
          lockedName
            ? { helperText: 'Default collar of this chasuble.' }
            : undefined
        }
      />
      <RhfFileInput<CollarFormValues>
        name="image"
        label="Image"
        accept={IMAGE_FILE_ACCEPT}
        initialPreviewSrc={getImageThumbnailUrl(collar)}
      />
    </FormDialog>
  );
};

export default CollarDialog;
